import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { C, FONTS } from '../../tokens/design';
import { useScanHistory } from '../../hooks/useClientData';
import { useClient } from '../../context/ClientContext';
import { useUser } from '../../context/UserContext';
import { useApiClient } from '../../api/apiClient';
import { relativeTime, exactTime } from '../../utils/time';
import { redactDemoText } from '../../utils/demoRedaction';
import Shell from '../../components/layout/Shell';
import Card, { CardHeader } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import LiveActionButton from '../../components/ui/LiveActionButton';
import EmptyState from '../../components/ui/EmptyState';
import Skeleton from '../../components/ui/Skeleton';
import Tag from '../../components/ui/Tag';
import PageError from '../../components/ui/PageError';
import { TH, TR, TD } from '../../components/ui/Table';

const STATUS_MAP = { 0: 'Queued', 1: 'Running', 2: 'Completed', 3: 'Failed' };
export const MAX_NESSUS_IMPORT_BYTES = 25 * 1024 * 1024;

export function validateNessusImportFile(file) {
  if (!file || !file.name || file.size <= 0) return 'Choose a non-empty .nessus export file.';
  if (!file.name.toLowerCase().endsWith('.nessus')) return 'Only .nessus export files are accepted.';
  if (file.size > MAX_NESSUS_IMPORT_BYTES) return 'Nessus import files are limited to 25 MB.';
  return null;
}

export function formatNessusImportResult(result = {}) {
  const severity = result.severity ?? {};
  const imported = Number(result.imported ?? 0);
  const hosts = Number(result.hosts ?? 0);
  return `Imported ${imported} finding${imported === 1 ? '' : 's'} across ${hosts} host${hosts === 1 ? '' : 's'} — C ${severity.critical ?? 0}, H ${severity.high ?? 0}, M ${severity.medium ?? 0}, L ${severity.low ?? 0}, I ${severity.info ?? 0}.`;
}
const NOISY_SCAN_STATUSES = new Set(['failed', 'error', 'canceled', 'cancelled', 'timeout', 'timedout', 'timed_out']);

const SCAN_ACTIONS = [
  { key: 'full', label: 'Full', queuedLabel: 'Full scan queued', profileName: 'full' },
  { key: 'cloud', label: 'Cloud', queuedLabel: 'Cloud scan queued', profileName: 'cloud', modules: ['cloud'] },
  { key: 'm365', label: 'Microsoft 365', queuedLabel: 'Microsoft 365 scan queued', profileName: 'm365', modules: ['m365'] },
  { key: 'osint', label: 'OSINT', queuedLabel: 'OSINT scan queued', profileName: 'osint', modules: ['osint'] },
  { key: 'nessus', label: 'Nessus', queuedLabel: 'Nessus import queued', profileName: 'nessus-import', modules: ['vuln-nessus'] },
  { key: 'zap', label: 'ZAP', queuedLabel: 'ZAP import queued', profileName: 'zap-import', modules: ['dast-zap-s3'] },
];

function normalizeStatus(s) {
  if (typeof s === 'number') return STATUS_MAP[s] ?? 'Unknown';
  if (typeof s === 'string' && s.length > 0) return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
  return 'Unknown';
}

function scanStatusKey(scan) {
  return normalizeStatus(scan?.status).toLowerCase().replace(/[\s-]+/g, '_');
}

export function isClientSafeScanHistoryRow(scan) {
  return !NOISY_SCAN_STATUSES.has(scanStatusKey(scan));
}

export function clientSafeScanHistory(scans = []) {
  const rows = [];
  const grouped = {
    failed: 0,
    canceled: 0,
    total: 0,
    latestAt: null,
  };

  for (const scan of scans) {
    if (isClientSafeScanHistoryRow(scan)) {
      rows.push(scan);
      continue;
    }

    grouped.total += 1;
    const status = scanStatusKey(scan);
    if (status === 'canceled' || status === 'cancelled') grouped.canceled += 1;
    else grouped.failed += 1;

    const scanTime = scan?.createdAt ?? scan?.startedAt ?? scan?.completedAt ?? scan?.endedAt ?? null;
    if (scanTime && (!grouped.latestAt || new Date(scanTime).getTime() > new Date(grouped.latestAt).getTime())) {
      grouped.latestAt = scanTime;
    }
  }

  return { rows, grouped };
}

const statusColor = (s) => {
  const sl = (s || '').toLowerCase();
  if (sl === 'completed' || sl === 'complete') return C.grn;
  if (sl === 'running' || sl === 'in_progress' || sl === 'inprogress') return C.sky;
  if (sl === 'failed' || sl === 'error') return C.red;
  if (sl === 'queued' || sl === 'pending') return C.amb;
  return C.t3;
};

function formatDuration(secs) {
  if (secs == null || secs < 0) return '\u2014';
  if (secs < 60) return `${Math.round(secs)}s`;
  const m = Math.floor(secs / 60);
  const s = Math.round(secs % 60);
  if (m < 60) return `${m}m ${s}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

export function scanHistoryDetail(scan) {
  const targets = Array.isArray(scan?.targets) ? scan.targets : [];
  const modules = Array.isArray(scan?.modules) ? scan.modules : [];
  const details = [];
  if (modules.length > 0) details.push(redactDemoText(modules.join(', ')));
  if (targets.length > 0) {
    details.push(`${targets.length} target${targets.length === 1 ? '' : 's'} hidden`);
  }
  return details.join(' - ');
}

export default function ScanHistory() {
  const navigate = useNavigate();
  const { data: scans, loading, error, refetch } = useScanHistory();
  const { clientId } = useClient();
  const { isPlatformAdmin, isMspUser } = useUser();
  const canWrite = isPlatformAdmin || isMspUser;
  const api = useApiClient();
  const fileInputRef = useRef(null);
  const [scanning, setScanning] = useState(false);
  const [uploadingNessus, setUploadingNessus] = useState(false);
  const [scanMsg, setScanMsg] = useState('');
  const { rows: clientVisibleScans, grouped: groupedScanAttempts } = clientSafeScanHistory(scans);

  const handleRunScan = async (action = SCAN_ACTIONS[0]) => {
    if (!clientId) return;
    setScanning(true);
    setScanMsg('');
    try {
      const body = { profileName: action.profileName };
      if (action.modules?.length) body.modules = action.modules;
      await api.post(`/clients/${clientId}/scans`, body);
      setScanMsg(action.queuedLabel);
      if (refetch) refetch();
    } catch (err) {
      setScanMsg(`Failed: ${err.message}`);
    } finally {
      setScanning(false);
    }
  };

  const handleNessusFileSelected = async (event) => {
    const file = event.target.files?.[0];
    const validationError = validateNessusImportFile(file);
    if (validationError) {
      setScanMsg(`Failed: ${validationError}`);
      event.target.value = '';
      return;
    }

    setUploadingNessus(true);
    setScanMsg('');
    try {
      const form = new FormData();
      form.append('file', file);
      const result = await api.post(`/clients/${clientId}/nessus/import`, form);
      setScanMsg(formatNessusImportResult(result));
      if (refetch) refetch();
    } catch (err) {
      setScanMsg(`Failed: ${err.message}`);
    } finally {
      setUploadingNessus(false);
      event.target.value = '';
    }
  };

  return (
    <Shell title="Scan Management" sub="Manual scan orchestration and job history">
      {error && <PageError error={error} onRetry={refetch} />}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Card>
          <CardHeader title="Recurring Schedules" action={
            <Button variant="sec" onClick={() => navigate('/client/scan-schedule')} style={{ fontSize: 10, padding: '5px 12px' }}>
              Open Schedule
            </Button>
          } />
          <div style={{ padding: '12px 14px', color: C.t2, fontSize: 11, lineHeight: 1.5 }}>
            Recurring scan cadence is managed on the dedicated schedule page. This view only starts one-time scans and shows recent scan history.
          </div>
        </Card>

        <Card>
          <CardHeader title="Scan History" action={
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              {scanMsg && <span style={{ fontSize: 9, color: scanMsg.startsWith('Failed') ? C.red : C.grn, fontFamily: FONTS.mono }}>{scanMsg}</span>}
              {canWrite && (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".nessus,application/vnd.tenable.nessus+xml,application/xml,text/xml"
                    onChange={handleNessusFileSelected}
                    style={{ display: 'none' }}
                    aria-label="Select Nessus export"
                  />
                  <Button
                    variant="sec"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingNessus || scanning || !clientId}
                    title="Import a complete Nessus export for the active client"
                    style={{ fontSize: 10, padding: '5px 10px' }}
                  >
                    {uploadingNessus ? 'Importing...' : 'Upload .nessus'}
                  </Button>
                  {SCAN_ACTIONS.map((action, index) => (
                    <LiveActionButton
                      key={action.key}
                      variant={index === 0 ? 'pri' : 'sec'}
                      onConfirm={() => handleRunScan(action)}
                      disabled={scanning || uploadingNessus || !clientId}
                      pending={scanning}
                      pendingLabel="Starting..."
                      confirmTitle={`Start ${action.label} scan`}
                      confirmDetail="This queues a one-time scan for the active client."
                      confirmLabel="Start"
                      style={{ fontSize: 10, padding: '5px 10px' }}
                    >
                      {action.label}
                    </LiveActionButton>
                  ))}
                </div>
              )}
              <span style={{ fontSize: 9, color: C.t3, fontFamily: FONTS.mono }}>{loading ? 'Loading...' : `${clientVisibleScans.length} scan${clientVisibleScans.length !== 1 ? 's' : ''}`}</span>
            </div>
          } />
          {!loading && groupedScanAttempts.total > 0 && (
            <div style={{ padding: '0 14px 12px' }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr auto',
                gap: 12,
                alignItems: 'center',
                background: `${C.amb}10`,
                border: `1px solid ${C.amb}33`,
                borderLeft: `4px solid ${C.amb}`,
                borderRadius: 8,
                padding: '11px 14px',
              }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 12, color: C.t1, fontWeight: 800, fontFamily: FONTS.sans }}>
                    Scan completion signal
                  </div>
                  <div style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono, lineHeight: 1.5, marginTop: 4 }}>
                    {groupedScanAttempts.failed > 0 && `${groupedScanAttempts.failed} unsuccessful scan attempt${groupedScanAttempts.failed === 1 ? '' : 's'}`}
                    {groupedScanAttempts.failed > 0 && groupedScanAttempts.canceled > 0 && ' and '}
                    {groupedScanAttempts.canceled > 0 && `${groupedScanAttempts.canceled} canceled request${groupedScanAttempts.canceled === 1 ? '' : 's'}`}
                    {' '}are grouped here so completed evidence remains easy to review.
                  </div>
                </div>
                <span style={{ fontSize: 9, color: C.t3, fontFamily: FONTS.mono, whiteSpace: 'nowrap' }} title={groupedScanAttempts.latestAt ? exactTime(groupedScanAttempts.latestAt) : ''}>
                  {groupedScanAttempts.latestAt ? `Latest ${relativeTime(groupedScanAttempts.latestAt)}` : 'Grouped'}
                </span>
              </div>
            </div>
          )}
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <TH cols={['Date', 'Profile', 'Status', 'Duration', 'Findings']} />
            <tbody>
              {loading && [0, 1, 2].map(i => (
                <tr key={i} style={{ borderBottom: `1px solid ${C.b2}` }}>
                  <TD><Skeleton width={110} height={12} /></TD>
                  <TD><Skeleton width={90} height={18} /></TD>
                  <TD><Skeleton width={65} height={18} /></TD>
                  <TD><Skeleton width={60} height={12} /></TD>
                  <TD><Skeleton width={30} height={12} /></TD>
                </tr>
              ))}
              {!loading && scans.length === 0 && (
                <tr><td colSpan={5}>
                  <EmptyState title="No scans yet" sub="Scan history will appear here once scans have been run for this client." />
                </td></tr>
              )}
              {!loading && scans.length > 0 && clientVisibleScans.length === 0 && (
                <tr><td colSpan={5}>
                  <EmptyState title="Scan attempts grouped" sub="Recent attempts are summarized above until completed evidence is available." />
                </td></tr>
              )}
              {!loading && clientVisibleScans.map(scan => {
                const id = scan.id ?? scan.scanId ?? '';
                const profile = redactDemoText(scan.label ?? scan.profileName ?? scan.scanType ?? 'General');
                const status = normalizeStatus(scan.status);
                const created = scan.createdAt ?? scan.startedAt ?? null;
                const completed = scan.completedAt ?? scan.endedAt ?? null;
                const started = scan.startedAt ?? scan.createdAt ?? null;
                const durationSeconds = scan.durationSeconds ?? (
                  started && completed
                    ? Math.max(0, (new Date(completed).getTime() - new Date(started).getTime()) / 1000)
                    : null
                );
                const findings = scan.findingsCount ?? scan.findingCount ?? 0;
                const sc = statusColor(status);
                const detail = scanHistoryDetail(scan);
                return (
                  <TR key={id || profile + created}>
                    <TD style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono, whiteSpace: 'nowrap' }} title={created ? exactTime(created) : ''}>
                      {created ? relativeTime(created) : '\u2014'}
                    </TD>
                    <TD style={{ fontSize: 11, color: C.t1, fontWeight: 500 }}>
                      <div>{profile}</div>
                      {detail && (
                        <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginTop: 2, wordBreak: 'break-all' }}>
                          {detail}
                        </div>
                      )}
                    </TD>
                    <TD><Tag label={status} color={sc} /></TD>
                    <TD style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono }}>{formatDuration(durationSeconds)}</TD>
                    <TD>
                      <span style={{ fontSize: 12, fontWeight: 700, color: findings > 0 ? C.org : C.t3, fontFamily: FONTS.mono }}>{findings}</span>
                    </TD>
                  </TR>
                );
              })}
            </tbody>
          </table>
        </Card>
      </div>
    </Shell>
  );
}
